import React, { useState, useEffect, useRef, useCallback } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";

export interface CompetitionOption {
  id: string;
  name: string;
  program?: {
    code?: string;
  } | null;
}

/**
 * Props for CompetitionAutocomplete component
 */
export interface CompetitionAutocompleteProps {
  /** List of available competitions to select from */
  competitions: CompetitionOption[];
  /** Currently selected competition ID (empty string if none) */
  value: string;
  /** Callback invoked when selected competition changes */
  onChange: (value: string) => void;
  /** Whether to display error styling (red border) */
  hasError?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether competition data is currently loading */
  isLoading?: boolean;
}

export const CompetitionAutocomplete: React.FC<CompetitionAutocompleteProps> = ({
  competitions,
  value,
  onChange,
  hasError = false,
  disabled = false,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCompetition = competitions.find((comp) => comp.id === value);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  useClickOutside(dropdownRef, handleClickOutside, isOpen);

  const filteredCompetitions = competitions.filter((competition) => {
    const query = searchQuery.toLowerCase();
    const matchesName = competition.name?.toLowerCase().includes(query);
    const matchesCode = competition.program?.code?.toLowerCase().includes(query);
    return matchesName || matchesCode;
  });

  const highlightText = (text: string, query: string) => {
    if (!query.trim() || !text) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  const handleCompetitionClick = (competitionId: string) => {
    onChange(competitionId);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = () => {
    onChange("");
    setIsOpen(false);
    setSearchQuery("");
  };

  const getDisplayName = (competition: CompetitionOption) => {
    if (competition.program?.code) {
      return `${competition.program.code} - ${competition.name}`;
    }
    return competition.name;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-left flex items-center justify-between ${
          hasError
            ? "border-red-500"
            : disabled
            ? "opacity-60 cursor-not-allowed border-neutral-300"
            : "border-neutral-300"
        }`}
      >
        <span className={selectedCompetition ? "text-neutral-900" : "text-neutral-500"}>
          {isLoading
            ? "Ładowanie..."
            : selectedCompetition
            ? getDisplayName(selectedCompetition)
            : "Wybierz konkurs"}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg z-[60] max-h-96 overflow-hidden flex flex-col">
          <div className="sticky top-0 bg-white border-b border-neutral-200 p-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsOpen(false);
                    setSearchQuery("");
                  }
                }}
                placeholder="Szukaj konkursu..."
                className="w-full pl-9 pr-8 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors border-b border-neutral-100 text-primary-600 font-medium text-sm"
              >
                Wyczyść wybór
              </button>
            )}

            {filteredCompetitions.length === 0 ? (
              <div className="text-center text-neutral-500 py-4 text-sm">
                Nie znaleziono konkursów
              </div>
            ) : (
              filteredCompetitions.map((competition) => (
                <button
                  key={competition.id}
                  type="button"
                  onClick={() => handleCompetitionClick(competition.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-b-0 ${
                    competition.id === value ? "bg-primary-50" : ""
                  }`}
                >
                  <div className="text-sm text-neutral-900">
                    {highlightText(getDisplayName(competition), searchQuery)}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
